<template>
  <div class="cl-container">
    <!-- ── Header del Control Operativo ── -->
    <div class="cl-page-header mb-5">
      <div class="d-flex align-center justify-space-between flex-wrap">
        <div>
          <v-btn @click="goRoute('/cajas')" text color="primary" class="cl-back-btn pl-0">
            <v-icon left small>mdi-chevron-left</v-icon>
            Regresar a puntos de venta
          </v-btn>
          <h1 class="cl-page-title">
            Control General de Cajas
            <span class="cl-page-subtitle">— Panel Operativo</span>
          </h1>
        </div>
        <v-btn @click="refreshAll" color="primary" outlined class="mt-2 text-none" dense>
          <v-icon left small>mdi-refresh</v-icon>
          Actualizar Datos
        </v-btn>
      </div>
    </div>

    <!-- ── KPI Cards / Métricas Operativas del Día ── -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card class="cl-kpi-card" elevation="1" rounded="lg">
          <div class="d-flex align-center justify-space-between pa-4">
            <div>
              <div class="cl-kpi-label">Cajas Abiertas</div>
              <div class="cl-kpi-value text-primary">{{ stats.abiertas }}</div>
            </div>
            <v-avatar color="#e3f2fd" size="44">
              <v-icon color="#1565c0">mdi-lock-open-variant-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="cl-kpi-card" elevation="1" rounded="lg">
          <div class="d-flex align-center justify-space-between pa-4">
            <div>
              <div class="cl-kpi-label">Cajas Cerradas</div>
              <div class="cl-kpi-value text-secondary">{{ stats.cerradas }}</div>
            </div>
            <v-avatar color="#eceff1" size="44">
              <v-icon color="#546e7a">mdi-lock-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="cl-kpi-card" elevation="1" rounded="lg">
          <div class="d-flex align-center justify-space-between pa-4">
            <div>
              <div class="cl-kpi-label">Aperturas (Rango)</div>
              <div class="cl-kpi-value text-info">{{ stats.aperturasRango }}</div>
            </div>
            <v-avatar color="#e0f7fa" size="44">
              <v-icon color="#00838f">mdi-login</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="cl-kpi-card" elevation="1" rounded="lg">
          <div class="d-flex align-center justify-space-between pa-4">
            <div>
              <div class="cl-kpi-label">Cierres (Rango)</div>
              <div class="cl-kpi-value text-success">{{ stats.cierresRango }}</div>
            </div>
            <v-avatar color="#e8f5e9" size="44">
              <v-icon color="#2e7d32">mdi-logout</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- ── Tabs: Histórico (Primero) y Sesiones Activas (Segundo) ── -->
    <v-card elevation="1" rounded="lg" class="mb-4">
      <v-tabs v-model="activeTab" background-color="white" color="primary" slider-size="3" class="cl-tabs">
        <v-tab class="cl-tab text-none font-weight-medium">
          <v-icon left small>mdi-history</v-icon>
          Histórico de Cierres y Aperturas
          <v-chip x-small color="primary lighten-5" text-color="primary" class="ml-2 font-weight-bold">
            {{ filteredCierres.length }}
          </v-chip>
        </v-tab>

        <v-tab class="cl-tab text-none font-weight-medium">
          <v-icon left small>mdi-monitor-dashboard</v-icon>
          Sesiones Activas (En Vivo)
          <v-chip x-small color="blue lighten-5" text-color="blue darken-2" class="ml-2 font-weight-bold">
            {{ data_cajas_activas.length }}
          </v-chip>
        </v-tab>
      </v-tabs>
    </v-card>

    <v-tabs-items v-model="activeTab" class="bg-transparent">
      <!-- ══════════ TAB 1: HISTÓRICO GENERAL DE OPERACIONES (PRIMERO) ══════════ -->
      <v-tab-item>
        <v-card class="cl-card" rounded="lg" elevation="1">
          <div class="cl-card-header">
            <div class="d-flex align-center">
              <v-icon left color="primary">mdi-history</v-icon>
              <span class="cl-card-title">Histórico General de Operaciones (Aperturas y Cierres)</span>
            </div>
            <v-chip small outlined color="grey darken-1">
              {{ filteredCierres.length }} registros
            </v-chip>
          </div>
          <v-divider></v-divider>

          <!-- Barra de Filtros Operativos -->
          <v-card-text class="cl-filter-section">
            <v-row dense align="center">
              <!-- Rango de Fechas -->
              <v-col cols="12" sm="6" md="3">
                <v-menu :close-on-content-click="false" max-width="290">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field :value="startDate" label="Fecha Inicial" readonly v-bind="attrs" v-on="on" outlined
                      dense hide-details prepend-inner-icon="mdi-calendar-start" class="cl-field"></v-text-field>
                  </template>
                  <v-date-picker v-model="startDate" @input="getCierre"></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" sm="6" md="3">
                <v-menu :close-on-content-click="false" max-width="290">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field :value="endDate" label="Fecha Final" readonly v-bind="attrs" v-on="on" outlined dense
                      hide-details prepend-inner-icon="mdi-calendar-end" class="cl-field"></v-text-field>
                  </template>
                  <v-date-picker v-model="endDate" @input="getCierre"></v-date-picker>
                </v-menu>
              </v-col>

              <!-- Filtro por Caja -->
              <v-col cols="12" sm="6" md="3">
                <v-select v-model="selectedCajaFilter" :items="cajasOptions" item-text="Nomb" item-value="id"
                  label="Filtrar por Caja" outlined dense hide-details prepend-inner-icon="mdi-cash-register"
                  class="cl-field"></v-select>
              </v-col>

              <!-- Filtro por Tipo (Apertura/Cierre) -->
              <v-col cols="12" sm="6" md="3">
                <v-select v-model="selectedTipoFilter" :items="tipoOptions" label="Tipo de Operación" outlined dense
                  hide-details prepend-inner-icon="mdi-filter-variant" class="cl-field"></v-select>
              </v-col>

              <!-- Búsqueda rápida & Botón -->
              <v-col cols="12" sm="9" md="10" class="mt-2">
                <v-text-field v-model="searchHistory" prepend-inner-icon="mdi-magnify"
                  label="Buscar por cajero, turno o ID..." outlined dense hide-details class="cl-field"></v-text-field>
              </v-col>

              <v-col cols="12" sm="3" md="2" class="mt-2">
                <v-btn @click="getCierre()" color="primary" depressed block class="cl-search-btn">
                  <v-icon left small>mdi-magnify</v-icon>
                  Consultar
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider></v-divider>

          <!-- Tabla de Histórico General -->
          <v-card-text class="pa-0">
            <v-data-table :headers="headersHistory" :items="filteredCierres" :loading="loadingHistory" class="cl-table"
              no-data-text="No se encontraron registros de aperturas ni cierres para el filtro seleccionado">
              <template v-slot:item.instalacion="{ item }">
                <div class="cl-instalacion-cell">
                  <span class="cl-instalacion-name">{{ item.instalacion }}</span>
                  <span class="cl-instalacion-meta">ID Registro: {{ item.id }}</span>
                  <span class="cl-instalacion-meta" v-if="item.Turno">Turno: {{ item.Turno }}</span>
                </div>
              </template>

              <template v-slot:item.CajeroNombre="{ item }">
                <div class="d-flex align-center">
                  <v-avatar color="primary lighten-5" size="24" class="mr-2">
                    <v-icon x-small color="primary">mdi-account</v-icon>
                  </v-avatar>
                  <span class="font-weight-medium">{{ item.CajeroNombre || 'Sin asignar' }}</span>
                </div>
              </template>

              <template v-slot:item.Momento="{ item }">
                <div class="d-flex align-center">
                  <v-icon small color="grey" class="mr-1">mdi-clock-outline</v-icon>
                  <span>{{ formatDate(item.Momento) }}</span>
                </div>
              </template>

              <template v-slot:item.Tipo="{ item }">
                <v-chip small label :color="item.Tipo === 'Cierre' ? '#e8f5e9' : '#e3f2fd'"
                  :text-color="item.Tipo === 'Cierre' ? '#2e7d32' : '#1565c0'" class="font-weight-medium">
                  <v-icon left x-small>
                    {{ item.Tipo === 'Cierre' ? 'mdi-lock-outline' : 'mdi-lock-open-variant-outline' }}
                  </v-icon>
                  {{ item.Tipo }}
                </v-chip>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn v-if="item.Tipo === 'Cierre'" @click="viewDetail(item)" small depressed color="primary"
                  class="cl-detail-btn text-none">
                  <v-icon small class="mr-1">mdi-eye-outline</v-icon>
                  Ver Detalle
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- ══════════ TAB 2: SESIONES ACTIVAS EN VIVO (SEGUNDO) ══════════ -->
      <v-tab-item>
        <v-card class="cl-card" rounded="lg" elevation="1">
          <div class="cl-card-header">
            <div class="d-flex align-center">
              <v-icon left color="primary">mdi-monitor-dashboard</v-icon>
              <span class="cl-card-title">Sesiones Activas en Vivo</span>
            </div>
            <v-chip small outlined color="primary">
              {{ data_cajas_activas.length }} registradas
            </v-chip>
          </div>
          <v-divider></v-divider>
          <v-card-text class="pa-4">
            <v-text-field v-model="searchActive" prepend-inner-icon="mdi-magnify" label="Buscar por caja o cajero..."
              outlined dense hide-details class="mb-4 cl-search-input"></v-text-field>

            <v-data-table :headers="headersActivas" :items="data_cajas_activas" :search="searchActive" class="cl-table"
              no-data-text="No hay sesiones de caja activas en este momento">
              <template v-slot:item.cajaNombre="{ item }">
                <div class="font-weight-bold color-dark">{{ item.cajaNombre }}</div>
                <div class="text-caption grey--text">ID Caja: {{ item.Caja }}</div>
              </template>

              <template v-slot:item.cajeroNombre="{ item }">
                <div class="d-flex align-center">
                  <v-avatar color="primary" size="24" class="mr-2">
                    <v-icon x-small dark>mdi-account</v-icon>
                  </v-avatar>
                  <span>{{ item.cajeroNombre }}</span>
                </div>
              </template>

              <template v-slot:item.Etts="{ item }">
                <v-chip small label :color="item.Etts === 'A' ? '#e3f2fd' : '#eceff1'"
                  :text-color="item.Etts === 'A' ? '#1565c0' : '#546e7a'" class="font-weight-medium">
                  <v-icon left x-small>{{ item.Etts === 'A' ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline'
                  }}</v-icon>
                  {{ item.Etts === 'A' ? 'Abierta' : 'Cerrada' }}
                </v-chip>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn @click="openActiveTotales(item)" small depressed color="primary" class="cl-detail-btn text-none"
                  :disabled="item.Etts !== 'A'">
                  <v-icon small class="mr-1">mdi-graph</v-icon>
                  Ver Totales
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <!-- Modal para CajaTotales -->
    <modal :ref="'modalForm'" v-model="showModal" :showFooter="false" title="Detalle de Caja">
      <CajaTotales :edit="editMode" :key="formIndex" @toggle="toggleModal" @closeAndReload="receiveReload"
        :dataEdit="editData" :CajaInstalacion="CajaInstalacion">
      </CajaTotales>
    </modal>
  </div>
</template>

<script>
import moment from "moment";
import modal from "../MainComponents/modal.vue";
import CajaTotales from "./CajaTotales.vue";

export default {
  name: "CortesList",
  components: {
    CajaTotales,
    modal,
  },
  data() {
    return {
      activeTab: 0,
      headersActivas: [
        { text: "Punto de Venta / Caja", value: "cajaNombre" },
        { text: "Cajero a Cargo", value: "cajeroNombre" },
        { text: "Identificador", value: "id" },
        { text: "Estatus", value: "Etts" },
        { text: "Acciones", value: "actions", sortable: false, align: "right" },
      ],
      headersHistory: [
        { text: "Punto de Venta", value: "instalacion" },
        { text: "Cajero", value: "CajeroNombre" },
        { text: "Departamento", value: "Departamentos" },
        { text: "Fecha / Hora", value: "Momento" },
        { text: "Tipo", value: "Tipo" },
        { text: "Acciones", value: "actions", sortable: false, align: "right" },
      ],

      startDate: new moment().format("YYYY-MM-DD"),
      endDate: new moment().format("YYYY-MM-DD"),

      searchActive: "",
      searchHistory: "",

      selectedCajaFilter: "TODAS",
      selectedTipoFilter: "TODOS",

      tipoOptions: [
        { text: "Todos los tipos", value: "TODOS" },
        { text: "Solo Aperturas", value: "Apertura" },
        { text: "Solo Cierres", value: "Cierre" },
      ],

      data_cajas_activas: [],
      data_cierres: [],
      cashiers: [],
      listaInstalaciones: [],

      loadingHistory: false,

      formIndex: 0,
      editMode: false,
      editData: {},
      showModal: false,
      CajaInstalacion: false,
    };
  },

  computed: {
    cajasOptions() {
      const options = [{ Nomb: "Todas las cajas", id: "TODAS" }];
      this.listaInstalaciones.forEach((c) => {
        options.push({ Nomb: c.Nomb, id: c.id });
      });
      return options;
    },

    stats() {
      const abiertas = this.data_cajas_activas.filter((c) => c.Etts === "A").length;
      const cerradas = this.data_cajas_activas.filter((c) => c.Etts !== "A").length;

      const aperturasRango = this.data_cierres.filter((c) => c.Tipo === "Apertura").length;
      const cierresRango = this.data_cierres.filter((c) => c.Tipo === "Cierre").length;

      return {
        abiertas,
        cerradas,
        aperturasRango,
        cierresRango,
      };
    },

    filteredCierres() {
      return this.data_cierres.filter((item) => {
        // Filtro por caja
        if (this.selectedCajaFilter !== "TODAS" && item.Caja !== this.selectedCajaFilter) {
          return false;
        }

        // Filtro por tipo
        if (this.selectedTipoFilter !== "TODOS" && item.Tipo !== this.selectedTipoFilter) {
          return false;
        }

        // Filtro por texto de búsqueda
        if (this.searchHistory) {
          const query = this.searchHistory.toLowerCase();
          const cajero = (item.CajeroNombre || "").toLowerCase();
          const inst = (item.instalacion || "").toLowerCase();
          const id = (item.id || "").toLowerCase();
          const turno = String(item.Turno || "").toLowerCase();

          return (
            cajero.includes(query) ||
            inst.includes(query) ||
            id.includes(query) ||
            turno.includes(query)
          );
        }

        return true;
      });
    },
  },

  methods: {
    goRoute(routePath) {
      this.$router.push({ path: routePath });
    },

    formatDate(momento) {
      if (!momento) return "—";
      return moment(momento).isValid()
        ? moment(momento).format("YYYY-MM-DD HH:mm")
        : momento;
    },

    async refreshAll() {
      await this.getInstalaciones();
      await this.getCashiers();
      await this.getCajasActivas();
      await this.getCierre();
    },

    async getInstalaciones() {
      const data = await this.$store.getters.getFirebaseApp.database.queryBuilder("PDV/Cajas");
      if (data.exists()) {
        this.listaInstalaciones = this.$pgsHelper.ArrayObjectToArray(data.val());
      } else {
        this.listaInstalaciones = [];
      }
    },

    async getCashiers() {
      const snapshot = await this.$store.getters.getFirebaseApp.database.queryBuilder("PDV/Cajeros");
      if (snapshot.exists()) {
        this.cashiers = [];
        Object.keys(snapshot.val()).forEach((id) => {
          const toAdd = snapshot.val()[id];
          if (toAdd) {
            toAdd.Id = id;
            this.cashiers.push(toAdd);
          }
        });
      }
    },

    async getCajasActivas() {
      const data = await this.$store.getters.getFirebaseApp.database.queryBuilder("PDV/CCSesion");
      if (!data.exists()) {
        this.data_cajas_activas = [];
        return;
      }
      let rawSesiones = this.$pgsHelper.ArrayObjectToArray(data.val());
      this.data_cajas_activas = rawSesiones.map((sesion) => {
        const cajaObj = this.$pgsHelper.getById(this.listaInstalaciones, "id", sesion.Caja);
        const cajeroObj = this.$pgsHelper.getById(this.cashiers, "Id", sesion.Cajero);
        return {
          ...sesion,
          cajaNombre: cajaObj ? cajaObj.Nomb : `Caja ID ${sesion.Caja}`,
          cajeroNombre: cajeroObj ? cajeroObj.Nomb : "Desconocido",
        };
      });
    },

    async getCierre() {
      this.loadingHistory = true;
      try {
        const startDate = this.startDate;
        const endDate = this.endDate;
        const end = endDate.replace(/.$/, (c) =>
          String.fromCharCode(c.charCodeAt(0) + 1)
        );
        const firestore = this.$store.getters.getFirebaseApp.firestore;

        const data = await firestore.queryBuilder("PDV_CierreApertura", [
          firestore.where("Momento", ">=", startDate),
          firestore.where("Momento", "<", end),
          firestore.where("Tenant", "==", this.$store.getters.getFirebaseApp.tenant),
          firestore.where("Tipo", "in", ["Apertura", "Cierre"]),
        ]);

        let cierres = data.docs.map((doc) => {
          return { id: doc.ref.id, ...doc.data() };
        });

        cierres.forEach((element) => {
          const cajeroObj = this.$pgsHelper.getById(this.cashiers, "Id", element.Cajero);
          const cajaObj = this.$pgsHelper.getById(this.listaInstalaciones, "id", element.Caja);

          element.CajeroNombre = cajeroObj ? cajeroObj.Nomb : element.Cajero || "Desconocido";
          element.instalacion = cajaObj ? cajaObj.Nomb : element.Caja || "Desconocida";
        });

        this.data_cierres = cierres;
      } finally {
        this.loadingHistory = false;
      }
    },

    openActiveTotales(item) {
      if (item.Etts !== "A") {
        this.$swal(
          "La caja no esta abierta",
          "No puedes ver los totales cuando la caja esta cerrada",
          "error"
        );
        return;
      }
      this.formIndex++;
      this.editMode = true;
      this.editData = this.$pgsHelper.cloneObj(item);
      this.CajaInstalacion = true;

      this.$refs.modalForm.setTitle(`Contadores en vivo — ${item.cajaNombre}`);
      this.toggleModal(true);
    },

    viewDetail(item) {
      this.formIndex++;
      this.editMode = true;
      this.editData = this.$pgsHelper.cloneObj(item);
      this.CajaInstalacion = false;

      this.$refs.modalForm.setTitle(`Detalle de Cierre — ${item.instalacion}`);
      this.toggleModal(true);
    },

    async receiveReload() {
      await this.refreshAll();
      this.toggleModal(false);
    },

    toggleModal(open) {
      this.showModal = open;
    },
  },

  async mounted() {
    await this.refreshAll();
  },
};
</script>

<style scoped>
/* ─── Container ─── */
.cl-container {
  font-family: "Poppins", sans-serif;

}

/* ─── Page Header ─── */
.cl-page-header {
  padding: 0 4px;
}

.cl-back-btn {
  font-size: 13px !important;
  text-transform: none !important;
  letter-spacing: 0.2px !important;
  margin-bottom: 4px;
}

.cl-page-title {
  font-size: 22px;
  font-weight: 700;
  color: #263238;
  margin: 0;
  line-height: 1.3;
}

.cl-page-subtitle {
  font-weight: 400;
  color: #78909c;
  font-size: 20px;
}

/* ─── KPI Cards ─── */
.cl-kpi-card {
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.cl-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.cl-kpi-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #78909c;
}

.cl-kpi-value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}

/* ─── Cards ─── */
.cl-card {
  overflow: hidden;
}

.cl-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #ffffff;
}

.cl-card-title {
  font-size: 16px;
  font-weight: 600;
  color: #37474f;
  letter-spacing: 0.2px;
}

/* ─── Filter Section ─── */
.cl-filter-section {
  background: #fafbfc;
  padding: 16px 20px !important;
}

.cl-field {
  background: white;
  border-radius: 8px;
}

.cl-search-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  border-radius: 8px !important;
  height: 40px !important;
}

/* ─── Table ─── */
.cl-table>>>.v-data-table-header th {
  font-size: 12px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  color: #546e7a !important;
  background: #f5f7f9 !important;
  border-bottom: 2px solid #e0e6eb !important;
  padding: 10px 16px !important;
}

.cl-table>>>tbody tr {
  transition: background-color 0.15s ease;
}

.cl-table>>>tbody tr:hover {
  background-color: #f5f9fc !important;
}

.cl-table>>>tbody td {
  padding: 12px 16px !important;
  font-size: 13px !important;
  color: #455a64 !important;
  border-bottom: 1px solid #f0f3f5 !important;
}

/* ─── Instalacion Cell ─── */
.cl-instalacion-cell {
  display: flex;
  flex-direction: column;
  padding: 4px 0;
}

.cl-instalacion-name {
  font-weight: 600;
  color: #263238;
  font-size: 13px;
  margin-bottom: 2px;
}

.cl-instalacion-meta {
  font-size: 11px;
  color: #90a4ae;
  line-height: 1.4;
}

/* ─── Buttons ─── */
.cl-detail-btn {
  border-radius: 6px !important;
  font-weight: 500 !important;
  letter-spacing: 0.2px !important;
}

/* ─── Responsive ─── */
@media (max-width: 600px) {
  .cl-page-title {
    font-size: 18px;
  }

  .cl-page-subtitle {
    font-size: 16px;
  }

  .cl-card-header {
    padding: 14px 16px;
  }
}
</style>
